
// frontend/app/components/NotificationsModal.js

// try 2 - BDD 01/22/2026
// frontend/app/components/NotificationsModal.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  StyleSheet,
  Platform,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import axios from "axios";

/**
 * NotificationsModal (DB-backed + fallback)
 *
 * Backend endpoints expected:
 * - GET /api/subscription/status
 * - GET /api/notifications/upcoming?days=120
 *
 * Behavior:
 * - Prefer DB jobs (notification_jobs) for schedule display.
 * - If DB jobs are empty or endpoint fails, fallback to computed schedule from access_end_date.
 */

function apiPath(path) {
  const p = String(path || "").replace(/^\/+/, "");
  const base = String(axios?.defaults?.baseURL || "");
  if (base.includes("/api")) return p; // base already has /api
  return `api/${p}`;
}

function parseDateOnly(v) {
  if (!v) return null;
  try {
    const s = String(v).trim();
    const ymd = s.slice(0, 10);
    const d = new Date(`${ymd}T00:00:00`);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

function fmtDate(isoOrDate) {
  try {
    if (!isoOrDate) return "";
    if (isoOrDate instanceof Date) return isoOrDate.toISOString().slice(0, 10);
    return String(isoOrDate).slice(0, 10);
  } catch {
    return "";
  }
}

function fmtDateTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return String(iso);
  }
}

function daysBetween(a, b) {
  // b - a in days, ceil
  const ms = b.getTime() - a.getTime();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

function labelForOffset(off) {
  const n = Number(off);
  if (!Number.isFinite(n)) return String(off);
  if (n === 0) return "T-0 (Expiry day)";
  if (n > 0) return `T+${n} (Grace notice)`;
  return `T${n}`; // e.g. T-15
}

function relativeWhen(d) {
  if (!d) return "";
  try {
    const today = new Date();
    const day0 = new Date(today.toISOString().slice(0, 10) + "T00:00:00");
    const diff = daysBetween(day0, d);
    if (diff === 0) return "Today";
    if (diff > 0) return `In ${diff} day(s)`;
    return `${Math.abs(diff)} day(s) ago`;
  } catch {
    return "";
  }
}

function describeEndDate(endDate, fallbackText = "") {
  if (fallbackText) return fallbackText;
  if (!endDate) return "";
  try {
    const today = new Date(new Date().toISOString().slice(0, 10) + "T00:00:00");
    const diff = daysBetween(today, endDate);
    if (diff > 0) return `Ends in ${diff} day(s)`;
    if (diff === 0) return "Expires today";
    return `Expired ${Math.abs(diff)} day(s) ago`;
  } catch {
    return "";
  }
}

function describeCutoffDate(cutoffDate, fallbackText = "") {
  if (fallbackText) return fallbackText;
  if (!cutoffDate) return "";
  try {
    const today = new Date(new Date().toISOString().slice(0, 10) + "T00:00:00");
    const diff = daysBetween(today, cutoffDate);
    if (diff > 0) return `Grace ends in ${diff} day(s)`;
    if (diff === 0) return "Grace ends today";
    return `Grace ended ${Math.abs(diff)} day(s) ago`;
  } catch {
    return "";
  }
}

function normalizeStatus(s) {
  const v = String(s || "").toLowerCase().trim();
  if (!v) return "unknown";
  return v;
}

function defaultOffsets() {
  return [-90, -60, -30, -15, 0, 14];
}

export default function NotificationsModal({
  visible,
  onClose,
  user,
  isDarkMode,
  t,
  onOpenSupport,
}) {
  const styles = useMemo(() => makeStyles(isDarkMode), [isDarkMode]);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // subscription/status endpoint response
  const [subStatus, setSubStatus] = useState(null);

  // upcoming jobs endpoint response
  const [upcoming, setUpcoming] = useState(null);

  const daysParam = 120;

  const refresh = async () => {
    try {
      setBusy(true);
      setErr("");

      const [st, up] = await Promise.all([
        axios.get(apiPath("subscription/status")),
        axios.get(apiPath(`notifications/upcoming?days=${daysParam}`)),
      ]);

      setSubStatus(st?.data || null);
      setUpcoming(up?.data || null);
    } catch (e) {
      // fall back to computed UI (still show something)
      const msg = e?.response?.data?.detail || String(e);
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!visible) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // ---------- Derived subscription display ----------
  const derived = useMemo(() => {
    // prefer backend status endpoint if available
    const graceDays =
      Number(subStatus?.grace_days ?? user?.grace_days ?? 14) || 14;

    const effectiveEnd = parseDateOnly(
      subStatus?.effective_end_date || user?.access_end_date
    );
    const effectiveCutoff = parseDateOnly(
      subStatus?.effective_cutoff_date || user?.access_cutoff_date
    );

    const status = normalizeStatus(subStatus?.status || "unknown");
    const scopeType =
      subStatus?.scope_type || (user?.school_id ? "school" : "user");
    const scopeKey =
      subStatus?.scope_key ||
      (user?.school_id ? `school:${user.school_id}` : `user:${(user?.email || "").toLowerCase()}`);

    const offsets =
      Array.isArray(subStatus?.notice_offsets_days) && subStatus.notice_offsets_days.length
        ? subStatus.notice_offsets_days.map((x) => Number(x)).filter((x) => Number.isFinite(x))
        : defaultOffsets();

    const recipients = subStatus?.recipients || null;
    const endStatusText = describeEndDate(
      effectiveEnd,
      subStatus?.end_status_text || user?.subscription_end_status_text || ""
    );
    const cutoffStatusText = describeCutoffDate(
      effectiveCutoff,
      subStatus?.cutoff_status_text || user?.subscription_cutoff_status_text || ""
    );

    return {
      graceDays,
      effectiveEnd,
      effectiveCutoff,
      status,
      daysToEnd: null,
      daysToCutoff: null,
      scopeType,
      scopeKey,
      offsets,
      recipients,
      endStatusText,
      cutoffStatusText,
    };
  }, [subStatus, user]);

  // ---------- DB-backed schedule ----------
  const dbSchedule = useMemo(() => {
    const items = upcoming?.items || [];
    if (!Array.isArray(items)) return [];
    // only keep renewal notices (in case endpoint later includes more types)
    return items
      .filter((it) => it?.event_type === "RENEWAL_NOTICE")
      .map((it) => {
        const payload = it?.payload || {};
        const off = payload?.offset_days;
        return {
          id: it.id,
          scheduled_for: it.scheduled_for,
          scheduled_for_label: fmtDateTime(it.scheduled_for),
          scope_key: it.scope_key,
          offset_days: typeof off === "number" ? off : payload?.offset_days,
          access_end_date: payload?.access_end_date,
          recipients: it?.recipients || null,
        };
      })
      .sort((a, b) => String(a.scheduled_for).localeCompare(String(b.scheduled_for)));
  }, [upcoming]);

  // ---------- Fallback computed schedule ----------
  const computedSchedule = useMemo(() => {
    if (!derived.effectiveEnd) return [];
    const end = derived.effectiveEnd;
    const today = new Date(new Date().toISOString().slice(0, 10) + "T00:00:00");

    // show within same window as backend job generator
    const windowStart = new Date(today.getTime() - 3 * 86400000);
    const windowEnd = new Date(today.getTime() + 120 * 86400000);

    return (derived.offsets || defaultOffsets())
      .map((off) => {
        const d = new Date(end.getTime() + Number(off) * 86400000);
        return { offset_days: Number(off), date: d, ymd: fmtDate(d) };
      })
      .filter((x) => x.date >= windowStart && x.date <= windowEnd)
      .sort((a, b) => a.date - b.date)
      .map((x) => ({
        ...x,
        when: relativeWhen(x.date),
      }));
  }, [derived.effectiveEnd, derived.offsets]);

  const statusPill = useMemo(() => {
    const s = derived.status;
    if (s === "active") return { text: "Active", style: styles.pillActive };
    if (s === "expiring_soon")
      return { text: "Expiring Soon", style: styles.pillExpiring };
    if (s === "in_grace") return { text: "In Grace", style: styles.pillGrace };
    if (s === "expired") return { text: "Expired", style: styles.pillExpired };
    return { text: "Unknown", style: styles.pillUnknown };
  }, [derived.status, styles]);

  const recipientsLine = useMemo(() => {
    // Prefer backend-provided recipients summary (from /api/subscription/status)
    const r = derived.recipients;
    if (!r) {
      // fallback (simple)
      return "Saras3D support/admin team will also receive these notices.";
    }
    const parts = [];
    parts.push(`Recipients: ${r.recipients_count ?? "?"}`);
    if (typeof r.watchers_count === "number") parts.push(`Watchers: ${r.watchers_count}`);
    if (typeof r.schooladmin_count === "number" && r.schooladmin_count > 0)
      parts.push(`School admins: ${r.schooladmin_count}`);
    if (typeof r.org_admin_count === "number" && r.org_admin_count > 0)
      parts.push(`Org admins: ${r.org_admin_count}`);
    if (typeof r.partner_count === "number" && r.partner_count > 0)
      parts.push(`Partner: ${r.partner_count}`);
    return parts.join(" • ");
  }, [derived.recipients]);

  const closeAll = () => onClose?.();

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={closeAll}>
      <View style={styles.backdrop}>
        <View style={styles.card}>
          {/* Top bar */}
          <View style={styles.topBar}>
            <View style={{ flexDirection: "row", alignItems: "center" }}>
              <MaterialCommunityIcons name="bell-outline" size={20} color={styles._c.text} />
              <Text style={styles.title}>{t?.("Notifications") || "Notifications"}</Text>
            </View>

            <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
              <TouchableOpacity onPress={refresh} style={styles.smallBtn} disabled={busy}>
                <MaterialCommunityIcons name="refresh" size={16} color={styles._c.text} />
                <Text style={styles.smallBtnText}>{t?.("Refresh") || "Refresh"}</Text>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={() => {
                  onClose?.();
                  onOpenSupport?.();
                }}
                style={styles.smallBtn}
              >
                <MaterialCommunityIcons name="lifebuoy" size={16} color={styles._c.text} />
                <Text style={styles.smallBtnText}>{t?.("Support") || "Support"}</Text>
              </TouchableOpacity>

              <TouchableOpacity onPress={closeAll} style={styles.closeBtn}>
                <MaterialCommunityIcons name="close" size={20} color={styles._c.text} />
              </TouchableOpacity>
            </View>
          </View>

          <ScrollView style={{ padding: 12 }}>
            {/* error */}
            {err ? (
              <View style={styles.errorBox}>
                <Text style={styles.errorText}>
                  {t?.("Some data could not be loaded. Showing fallback where possible.") ||
                    "Some data could not be loaded. Showing fallback where possible."}
                </Text>
                <Text style={styles.errorTextSmall}>{String(err)}</Text>
              </View>
            ) : null}

            {/* busy */}
            {busy ? (
              <View style={{ padding: 12 }}>
                <ActivityIndicator />
              </View>
            ) : null}

            {/* Subscription */}
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <MaterialCommunityIcons name="calendar-clock" size={18} color={styles._c.text} />
                <Text style={styles.sectionTitle}>{t?.("Subscription") || "Subscription"}</Text>
                <View style={[styles.pill, statusPill.style]}>
                  <Text style={styles.pillText}>{statusPill.text}</Text>
                </View>
              </View>

              {derived.effectiveEnd ? (
                <>
                  <Text style={styles.rowText}>
                    <Text style={styles.bold}>{t?.("End date") || "End date"}: </Text>
                    {fmtDate(derived.effectiveEnd)}
                    {typeof derived.daysToEnd === "number" ? `  •  ${derived.daysToEnd} day(s)` : ""}
                  </Text>
                  {derived.endStatusText ? <Text style={styles.muted}>{derived.endStatusText}</Text> : null}

                  <Text style={styles.rowText}>
                    <Text style={styles.bold}>{t?.("Grace days") || "Grace days"}: </Text>
                    {derived.graceDays}
                  </Text>
                  {derived.cutoffStatusText ? <Text style={styles.muted}>{derived.cutoffStatusText}</Text> : null}

                  <Text style={styles.rowText}>
                    <Text style={styles.bold}>{t?.("Cutoff date") || "Cutoff date"}: </Text>
                    {derived.effectiveCutoff ? fmtDate(derived.effectiveCutoff) : "(unknown)"}
                    {typeof derived.daysToCutoff === "number" ? `  •  ${derived.daysToCutoff} day(s)` : ""}
                  </Text>

                  <Text style={styles.muted}>{recipientsLine}</Text>
                </>
              ) : (
                <Text style={styles.muted}>
                  {t?.(
                    "Subscription info is not available yet. It will appear after login once access_end_date is returned by auth."
                  ) ||
                    "Subscription info is not available yet. It will appear after login once access_end_date is returned by auth."}
                </Text>
              )}
            </View>

            {/* DB-backed schedule */}
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <MaterialCommunityIcons name="bell-badge-outline" size={18} color={styles._c.text} />
                <Text style={styles.sectionTitle}>
                  {t?.("Renewal Notice Schedule") || "Renewal Notice Schedule"}
                </Text>
                <View style={styles.tag}>
                  <Text style={styles.tagText}>{t?.("From DB") || "From DB"}</Text>
                </View>
              </View>

              {dbSchedule.length === 0 ? (
                <Text style={styles.muted}>
                  {t?.("No scheduled notices found in DB yet. Showing computed schedule below.") ||
                    "No scheduled notices found in DB yet. Showing computed schedule below."}
                </Text>
              ) : (
                dbSchedule.map((it) => {
                  const off = it.offset_days;
                  const label = labelForOffset(off);
                  const when = it.scheduled_for ? relativeWhen(new Date(it.scheduled_for)) : "";
                  const rec = it.recipients;

                  return (
                    <View key={String(it.id)} style={styles.noticeRow}>
                      <View style={{ flex: 1 }}>
                        <Text style={styles.noticeTitle}>{label}</Text>
                        <Text style={styles.noticeMeta}>
                          {fmtDateTime(it.scheduled_for)} {when ? `• ${when}` : ""}
                        </Text>
                        {rec ? (
                          <Text style={styles.noticeMeta2}>
                            {`Recipients: ${rec.recipients_count ?? "?"} • Watchers: ${rec.watchers_count ?? "?"}${
                              typeof rec.schooladmin_count === "number" && rec.schooladmin_count > 0
                                ? ` • School admins: ${rec.schooladmin_count}`
                                : ""
                            }`}
                          </Text>
                        ) : null}
                      </View>
                      <MaterialCommunityIcons name="chevron-right" size={18} color={styles._c.muted} />
                    </View>
                  );
                })
              )}
            </View>

            {/* Fallback computed schedule */}
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <MaterialCommunityIcons name="calculator-variant-outline" size={18} color={styles._c.text} />
                <Text style={styles.sectionTitle}>{t?.("Computed Schedule") || "Computed Schedule"}</Text>
                <View style={styles.tagMuted}>
                  <Text style={styles.tagTextMuted}>{t?.("Fallback") || "Fallback"}</Text>
                </View>
              </View>

              {!derived.effectiveEnd ? (
                <Text style={styles.muted}>
                  {t?.("Computed schedule needs a subscription end date.") || "Computed schedule needs a subscription end date."}
                </Text>
              ) : computedSchedule.length === 0 ? (
                <Text style={styles.muted}>
                  {t?.("No computed notices in the next 120 days.") || "No computed notices in the next 120 days."}
                </Text>
              ) : (
                computedSchedule.map((it) => (
                  <View key={`${it.ymd}_${it.offset_days}`} style={styles.noticeRow}>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.noticeTitle}>{labelForOffset(it.offset_days)}</Text>
                      <Text style={styles.noticeMeta}>
                        {it.ymd} • {it.when}
                      </Text>
                    </View>
                    <MaterialCommunityIcons name="chevron-right" size={18} color={styles._c.muted} />
                  </View>
                ))
              )}

              <Text style={[styles.muted, { marginTop: 10 }]}>
                {t?.(
                  "Computed schedule is for visibility. DB schedule is the source of truth for cron-driven notices."
                ) ||
                  "Computed schedule is for visibility. DB schedule is the source of truth for cron-driven notices."}
              </Text>
            </View>

            {/* Info */}
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <MaterialCommunityIcons name="information-outline" size={18} color={styles._c.text} />
                <Text style={styles.sectionTitle}>{t?.("Info") || "Info"}</Text>
              </View>
              <Text style={styles.muted}>
                {`User: ${user?.email || "?"}\nRole: ${user?.role || "?"}\nSchool ID: ${
                  user?.school_id ?? "NULL"
                }\nScope: ${derived.scopeType} (${derived.scopeKey})\nPlatform: ${Platform.OS}`}
              </Text>
            </View>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

function makeStyles(isDark) {
  const c = {
    bg: isDark ? "#0b1220" : "#f3f4f6",
    card: isDark ? "#0f172a" : "#ffffff",
    border: isDark ? "#22324a" : "#e5e7eb",
    text: isDark ? "#e5e7eb" : "#111827",
    muted: isDark ? "#9ca3af" : "#6b7280",
    errorBg: isDark ? "#3b0d0d" : "#fee2e2",
    errorText: isDark ? "#fecaca" : "#991b1b",
    primary: "#2563eb",
  };

  const s = StyleSheet.create({
    _c: c,
    backdrop: {
      flex: 1,
      backgroundColor: "rgba(0,0,0,0.55)",
      justifyContent: "center",
      padding: 16,
    },
    card: {
      backgroundColor: c.card,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: c.border,
      overflow: "hidden",
      maxHeight: "90%",
    },
    topBar: {
      paddingHorizontal: 12,
      paddingVertical: 10,
      borderBottomWidth: 1,
      borderBottomColor: c.border,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      backgroundColor: c.bg,
    },
    title: { color: c.text, fontWeight: "800", marginLeft: 8, fontSize: 16 },

    closeBtn: {
      width: 34,
      height: 34,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: c.border,
      alignItems: "center",
      justifyContent: "center",
    },

    smallBtn: {
      flexDirection: "row",
      gap: 6,
      alignItems: "center",
      paddingHorizontal: 10,
      paddingVertical: 6,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: c.border,
    },
    smallBtnText: { color: c.text, fontWeight: "700", fontSize: 12 },

    errorBox: {
      backgroundColor: c.errorBg,
      borderColor: c.border,
      borderWidth: 1,
      padding: 10,
      borderRadius: 10,
      marginBottom: 12,
    },
    errorText: { color: c.errorText, fontWeight: "900" },
    errorTextSmall: { color: c.errorText, marginTop: 6, fontWeight: "700" },

    section: {
      borderWidth: 1,
      borderColor: c.border,
      borderRadius: 12,
      padding: 12,
      marginBottom: 12,
      backgroundColor: isDark ? "#0b1220" : "#f9fafb",
    },
    sectionHeader: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      marginBottom: 8,
    },
    sectionTitle: { color: c.text, fontWeight: "900", fontSize: 14, flex: 1 },

    pill: {
      paddingHorizontal: 10,
      paddingVertical: 4,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: c.border,
    },
    pillText: { color: c.text, fontWeight: "900", fontSize: 12 },

    pillActive: { backgroundColor: "rgba(34,197,94,0.14)", borderColor: "rgba(34,197,94,0.5)" },
    pillExpiring: { backgroundColor: "rgba(245,158,11,0.14)", borderColor: "rgba(245,158,11,0.5)" },
    pillGrace: { backgroundColor: "rgba(249,115,22,0.14)", borderColor: "rgba(249,115,22,0.5)" },
    pillExpired: { backgroundColor: "rgba(239,68,68,0.14)", borderColor: "rgba(239,68,68,0.5)" },
    pillUnknown: { backgroundColor: "rgba(148,163,184,0.14)", borderColor: "rgba(148,163,184,0.5)" },

    tag: {
      paddingHorizontal: 8,
      paddingVertical: 3,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: "rgba(37,99,235,0.45)",
      backgroundColor: "rgba(37,99,235,0.14)",
    },
    tagText: { color: c.text, fontWeight: "900", fontSize: 12 },

    tagMuted: {
      paddingHorizontal: 8,
      paddingVertical: 3,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: c.border,
      backgroundColor: "transparent",
    },
    tagTextMuted: { color: c.muted, fontWeight: "900", fontSize: 12 },

    rowText: { color: c.text, marginTop: 2, fontSize: 13 },
    bold: { fontWeight: "900", color: c.text },
    muted: { color: c.muted, marginTop: 6, lineHeight: 18 },

    noticeRow: {
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: 10,
      borderTopWidth: 1,
      borderTopColor: c.border,
    },
    noticeTitle: { color: c.text, fontWeight: "900" },
    noticeMeta: { color: c.muted, marginTop: 2, fontSize: 12 },
    noticeMeta2: { color: c.muted, marginTop: 4, fontSize: 12 },
  });

  s._c = c;
  return s;
}

// frontend/app/components/NotificationsModal.js
// import React, { useMemo } from "react";
// import { Modal, View, Text, TouchableOpacity, ScrollView, StyleSheet, Platform } from "react-native";
// import { MaterialCommunityIcons } from "@expo/vector-icons";

// const DEFAULT_OFFSETS = [-90, -60, -30, -15, 0, 14];

// function isoDate(d) {
//   try {
//     return d.toISOString().slice(0, 10);
//   } catch {
//     return "";
//   }
// }

// function parseDateOnly(v) {
//   if (!v) return null;
//   try {
//     const s = String(v).trim();
//     const ymd = s.slice(0, 10);
//     const d = new Date(`${ymd}T00:00:00`);
//     return isNaN(d.getTime()) ? null : d;
//   } catch {
//     return null;
//   }
// }

// function daysBetween(a, b) {
//   // b - a in days
//   const ms = b.getTime() - a.getTime();
//   return Math.ceil(ms / (1000 * 60 * 60 * 24));
// }

// function labelForOffset(off) {
//   if (off === 0) return "T-0 (Expiry day)";
//   if (off > 0) return `T+${off} (Grace notice)`;
//   return `T${off}`; // e.g. T-15
// }

// export default function NotificationsModal({ visible, onClose, user, isDarkMode, t, onOpenSupport }) {
//   const styles = useMemo(() => makeStyles(isDarkMode), [isDarkMode]);

//   const graceDays = Number(user?.grace_days ?? 14);
//   const endDate = parseDateOnly(user?.access_end_date);
//   const cutoffDate = parseDateOnly(user?.access_cutoff_date);
//   const today = new Date();

//   const subscription = (() => {
//     if (!endDate) {
//       return {
//         available: false,
//         status: "Unknown",
//         statusHint: "Subscription info will appear after your next login once the auth update is deployed.",
//         daysLeft: null,
//       };
//     }

//     const daysToEnd = daysBetween(today, endDate);
//     const cutoff = cutoffDate || new Date(endDate.getTime() + graceDays * 86400000);
//     const daysToCutoff = daysBetween(today, cutoff);

//     let status = "Active";
//     let hint = "Your access is active.";
//     if (daysToEnd < 0 && daysToCutoff >= 0) {
//       status = "In Grace";
//       hint = `Subscription ended on ${isoDate(endDate)}. Grace ends on ${isoDate(cutoff)}.`;
//     } else if (daysToCutoff < 0) {
//       status = "Expired";
//       hint = `Subscription + grace ended on ${isoDate(cutoff)}. Login should be blocked.`;
//     } else if (daysToEnd <= 30) {
//       status = "Expiring Soon";
//       hint = `Expires in ${daysToEnd} day(s) on ${isoDate(endDate)}.`;
//     }

//     return {
//       available: true,
//       status,
//       statusHint: hint,
//       daysLeft: daysToEnd,
//       end: endDate,
//       cutoff,
//     };
//   })();

//   const schedule = (() => {
//     if (!endDate) return [];
//     // Show notices within a reasonable window around today
//     const windowStart = new Date(today.getTime() - 3 * 86400000);
//     const windowEnd = new Date(today.getTime() + 120 * 86400000);

//     return DEFAULT_OFFSETS
//       .map((off) => {
//         const d = new Date(endDate.getTime() + off * 86400000);
//         return { off, date: d, ymd: isoDate(d) };
//       })
//       .filter((x) => x.date >= windowStart && x.date <= windowEnd)
//       .sort((a, b) => a.date - b.date);
//   })();

//   return (
//     <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
//       <View style={styles.backdrop}>
//         <View style={styles.card}>
//           {/* Top bar */}
//           <View style={styles.topBar}>
//             <View style={{ flexDirection: "row", alignItems: "center" }}>
//               <MaterialCommunityIcons name="bell-outline" size={20} color={styles._c.text} />
//               <Text style={styles.title}>{t?.("Notifications") || "Notifications"}</Text>
//             </View>

//             <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
//               <TouchableOpacity onPress={onOpenSupport} style={styles.smallBtn}>
//                 <MaterialCommunityIcons name="lifebuoy" size={16} color={styles._c.text} />
//                 <Text style={styles.smallBtnText}>{t?.("Support") || "Support"}</Text>
//               </TouchableOpacity>

//               <TouchableOpacity onPress={onClose} style={styles.closeBtn}>
//                 <MaterialCommunityIcons name="close" size={20} color={styles._c.text} />
//               </TouchableOpacity>
//             </View>
//           </View>

//           <ScrollView style={{ padding: 12 }}>
//             {/* Subscription card */}
//             <View style={styles.section}>
//               <View style={styles.sectionHeader}>
//                 <MaterialCommunityIcons name="calendar-clock" size={18} color={styles._c.text} />
//                 <Text style={styles.sectionTitle}>{t?.("Subscription") || "Subscription"}</Text>
//                 <View style={styles.pill}>
//                   <Text style={styles.pillText}>{subscription.status}</Text>
//                 </View>
//               </View>

//               {!subscription.available ? (
//                 <Text style={styles.muted}>{subscription.statusHint}</Text>
//               ) : (
//                 <>
//                   <Text style={styles.rowText}>
//                     <Text style={styles.bold}>{t?.("End date") || "End date"}: </Text>
//                     {isoDate(subscription.end)}
//                   </Text>

//                   <Text style={styles.rowText}>
//                     <Text style={styles.bold}>{t?.("Grace days") || "Grace days"}: </Text>
//                     {graceDays}
//                   </Text>

//                   <Text style={styles.rowText}>
//                     <Text style={styles.bold}>{t?.("Cutoff date") || "Cutoff date"}: </Text>
//                     {isoDate(subscription.cutoff)}
//                   </Text>

//                   <Text style={styles.muted}>{subscription.statusHint}</Text>
//                 </>
//               )}
//             </View>

//             {/* Schedule card */}
//             <View style={styles.section}>
//               <View style={styles.sectionHeader}>
//                 <MaterialCommunityIcons name="bell-badge-outline" size={18} color={styles._c.text} />
//                 <Text style={styles.sectionTitle}>{t?.("Renewal Notice Schedule") || "Renewal Notice Schedule"}</Text>
//               </View>

//               {!endDate ? (
//                 <Text style={styles.muted}>
//                   {t?.("Schedule will appear once subscription end date is available.") ||
//                     "Schedule will appear once subscription end date is available."}
//                 </Text>
//               ) : schedule.length === 0 ? (
//                 <Text style={styles.muted}>
//                   {t?.("No notices in the next 120 days.") || "No notices in the next 120 days."}
//                 </Text>
//               ) : (
//                 schedule.map((it) => {
//                   const d = it.date;
//                   const diff = daysBetween(today, d);
//                   const when =
//                     diff === 0 ? "Today" : diff > 0 ? `In ${diff} day(s)` : `${Math.abs(diff)} day(s) ago`;

//                   return (
//                     <View key={it.ymd + it.off} style={styles.noticeRow}>
//                       <View style={{ flex: 1 }}>
//                         <Text style={styles.noticeTitle}>{labelForOffset(it.off)}</Text>
//                         <Text style={styles.noticeMeta}>
//                           {it.ymd} • {when}
//                         </Text>
//                       </View>
//                       <MaterialCommunityIcons name="chevron-right" size={18} color={styles._c.muted} />
//                     </View>
//                   );
//                 })
//               )}

//               <Text style={[styles.muted, { marginTop: 10 }]}>
//                 {t?.("These are calculated in the app. Emails will be enabled later; internal admins are already tracked as watchers in backend.") ||
//                   "These are calculated in the app. Emails will be enabled later; internal admins are already tracked as watchers in backend."}
//               </Text>
//             </View>

//             {/* Small debug info */}
//             <View style={styles.section}>
//               <View style={styles.sectionHeader}>
//                 <MaterialCommunityIcons name="information-outline" size={18} color={styles._c.text} />
//                 <Text style={styles.sectionTitle}>{t?.("Info") || "Info"}</Text>
//               </View>
//               <Text style={styles.muted}>
//                 {`User: ${user?.email || "?"}\nRole: ${user?.role || "?"}\nSchool ID: ${user?.school_id ?? "NULL"}\nPlatform: ${Platform.OS}`}
//               </Text>
//             </View>
//           </ScrollView>
//         </View>
//       </View>
//     </Modal>
//   );
// }

// function makeStyles(isDark) {
//   const c = {
//     bg: isDark ? "#0b1220" : "#f3f4f6",
//     card: isDark ? "#0f172a" : "#ffffff",
//     border: isDark ? "#22324a" : "#e5e7eb",
//     text: isDark ? "#e5e7eb" : "#111827",
//     muted: isDark ? "#9ca3af" : "#6b7280",
//     primary: "#2563eb",
//   };

//   const s = StyleSheet.create({
//     _c: c,
//     backdrop: {
//       flex: 1,
//       backgroundColor: "rgba(0,0,0,0.55)",
//       justifyContent: "center",
//       padding: 16,
//     },
//     card: {
//       backgroundColor: c.card,
//       borderRadius: 12,
//       borderWidth: 1,
//       borderColor: c.border,
//       overflow: "hidden",
//       maxHeight: "90%",
//     },
//     topBar: {
//       paddingHorizontal: 12,
//       paddingVertical: 10,
//       borderBottomWidth: 1,
//       borderBottomColor: c.border,
//       flexDirection: "row",
//       alignItems: "center",
//       justifyContent: "space-between",
//       backgroundColor: c.bg,
//     },
//     title: { color: c.text, fontWeight: "800", marginLeft: 8, fontSize: 16 },
//     closeBtn: {
//       width: 34,
//       height: 34,
//       borderRadius: 10,
//       borderWidth: 1,
//       borderColor: c.border,
//       alignItems: "center",
//       justifyContent: "center",
//     },
//     smallBtn: {
//       flexDirection: "row",
//       gap: 6,
//       alignItems: "center",
//       paddingHorizontal: 10,
//       paddingVertical: 6,
//       borderRadius: 10,
//       borderWidth: 1,
//       borderColor: c.border,
//     },
//     smallBtnText: { color: c.text, fontWeight: "700", fontSize: 12 },

//     section: {
//       borderWidth: 1,
//       borderColor: c.border,
//       borderRadius: 12,
//       padding: 12,
//       marginBottom: 12,
//       backgroundColor: isDark ? "#0b1220" : "#f9fafb",
//     },
//     sectionHeader: {
//       flexDirection: "row",
//       alignItems: "center",
//       gap: 8,
//       marginBottom: 8,
//     },
//     sectionTitle: { color: c.text, fontWeight: "900", fontSize: 14, flex: 1 },
//     pill: {
//       paddingHorizontal: 10,
//       paddingVertical: 4,
//       borderRadius: 999,
//       borderWidth: 1,
//       borderColor: c.border,
//       backgroundColor: "rgba(37,99,235,0.12)",
//     },
//     pillText: { color: c.text, fontWeight: "800", fontSize: 12 },

//     rowText: { color: c.text, marginTop: 2, fontSize: 13 },
//     bold: { fontWeight: "900", color: c.text },
//     muted: { color: c.muted, marginTop: 6, lineHeight: 18 },

//     noticeRow: {
//       flexDirection: "row",
//       alignItems: "center",
//       paddingVertical: 10,
//       borderTopWidth: 1,
//       borderTopColor: c.border,
//     },
//     noticeTitle: { color: c.text, fontWeight: "900" },
//     noticeMeta: { color: c.muted, marginTop: 2, fontSize: 12 },
//   });

//   s._c = c;
//   return s;
// }
